<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800px"
    overlay-opacity="0.85"
    content-class="modern-availability-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header with gradient background -->
      <v-card-title class="editor-header d-flex justify-space-between align-center px-6 py-4">
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-calendar-clock</v-icon>
          Edit Availability
        </div>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          color="white"
          depressed
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <!-- Help text -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          Select the days and times you're available
        </div>
      </v-card-text>
      
      <!-- Error/Success message with smooth transition -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message">
          <v-alert
            :type="messageType"
            class="mb-4 elevation-2"
            transition="scale-transition"
            :icon="messageType === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'"
          >
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>
      
      <!-- Availability Table -->
      <v-card-text class="px-6 py-2">
        <v-data-table
          :headers="headers"
          :items="data"
          :loading="loading"
          class="elevation-0 availability-table"
          hide-default-footer
        >
          <template v-slot:[`item.day`]="{ item }">
            <div class="font-weight-medium">
              {{ item.day }}
            </div>
          </template>
          
          <template v-slot:[`item.morning`]="{ item }">
            <v-checkbox
              v-model="item.morning"
              color="primary"
              @change="onUpdateValue(item, 'morning')"
              class="justify-center"
            />
          </template>
          
          <template v-slot:[`item.afternoon`]="{ item }">
            <v-checkbox
              v-model="item.afternoon"
              color="primary"
              @change="onUpdateValue(item, 'afternoon')"
              class="justify-center"
            />
          </template>
          
          <template v-slot:[`item.evening`]="{ item }">
            <v-checkbox
              v-model="item.evening"
              color="primary"
              @change="onUpdateValue(item, 'evening')"
              class="justify-center"
            />
          </template>
          
          <template v-slot:[`item.nothing`]="{ item }">
            <v-checkbox
              v-model="item.nothing"
              color="primary"
              @change="onUpdateValue(item, 'nothing')"
              class="justify-center"
            />
          </template>
        </v-data-table>
      </v-card-text>
      
      <!-- Action buttons -->
      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-space-between">
        <v-btn
          text
          @click="onClose"
          color="grey darken-1"
          class="px-6"
          large
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onSave"
          depressed
          width="160"
          :loading="loading"
          :disabled="!hasSelection"
          class="px-6 save-btn"
          large
          dark
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-content-save</v-icon>
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
export default {
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      message: null,
      messageType: 'error',
      loading: false,
      headers: [
        { text: "Day", value: "day", sortable: false, width: "20%" },
        { text: "Morning", value: "morning", sortable: false, align: "center", width: "20%" },
        { text: "Afternoon", value: "afternoon", sortable: false, align: "center", width: "20%" },
        { text: "Evening", value: "evening", sortable: false, align: "center", width: "20%" },
        { text: "Not Available", value: "nothing", sortable: false, align: "center", width: "20%" }
      ],
      data: [
        { day: "Monday", morning: false, afternoon: false, evening: false, nothing: false },
        { day: "Tuesday", morning: false, afternoon: false, evening: false, nothing: false },
        { day: "Wednesday", morning: false, afternoon: false, evening: false, nothing: false },
        { day: "Thursday", morning: false, afternoon: false, evening: false, nothing: false },
        { day: "Friday", morning: false, afternoon: false, evening: false, nothing: false },
        { day: "Saturday", morning: false, afternoon: false, evening: false, nothing: false },
        { day: "Sunday", morning: false, afternoon: false, evening: false, nothing: false }
      ]
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    }),
    hasSelection() {
      return this.data.some(day => 
        day.morning || day.afternoon || day.evening || day.nothing
      );
    }
  },
  methods: {
    ...mapActions("auth", {
      updateProfile: "updateProfile"
    }),
    onUpdateValue(item, key) {
      this.message = null;
      if (key === "nothing" && item[key]) {
        // If "Not Available" is checked, uncheck other options
        item.morning = false;
        item.afternoon = false;
        item.evening = false;
      } else if (item[key]) {
        // If any time slot is checked, uncheck "Not Available"
        item.nothing = false;
      }
    },
    async onSave() {
      if (!this.hasSelection) {
        this.message = "Please select at least one time slot";
        this.messageType = 'error';
        return;
      }
      
      this.loading = true;
      try {
        await this.updateProfile({ availabilities: this.data });
        this.message = "Availability updated successfully!";
        this.messageType = 'success';
        setTimeout(() => {
          this.onClose();
        }, 1500);
      } catch (error) {
        this.message = error.response?.data?.message || "An error occurred while saving";
        this.messageType = 'error';
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    if (this.profile?.availabilities) {
      // Map saved availabilities to our data structure
      this.data = this.data.map(day => {
        const savedDay = this.profile.availabilities.find(d => d.day === day.day);
        return savedDay ? { ...savedDay } : day;
      });
    }
  }
};
</script>

<style scoped>
.modern-availability-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255,255,255,0.2) !important;
}

.availability-table::v-deep table {
  width: 100%;
}

.availability-table::v-deep th {
  font-weight: 600;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.87);
}

.availability-table::v-deep td {
  padding: 12px 16px;
}

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-availability-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-availability-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-availability-dialog::-webkit-scrollbar-thumb {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-availability-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}
</style>